import { useEffect, useMemo, useState } from "react";

const ENDPOINT = "/.netlify/functions/availability";

const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
};

/** Turn a YYYY-MM-DD string into a local-midnight Date. */
const fromIso = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Loads Airbnb availability.
 *
 * Returns:
 *   bookedDates  Date[]  every unavailable night, for DayPicker `disabled`
 *   isBooked     (Date) => boolean
 *   rangeHasBooked (from, to) => boolean  true if any night in [from, to) is taken
 *   status       "loading" | "ready" | "error"
 */
export function useAvailability() {
  const [status, setStatus] = useState("loading");
  const [ranges, setRanges] = useState([]);

  useEffect(() => {
    let live = true;

    fetch(ENDPOINT)
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((data) => {
        if (!live) return;
        setRanges(Array.isArray(data.ranges) ? data.ranges : []);
        setStatus("ready");
      })
      .catch(() => {
        if (!live) return;
        // Fail open: an empty set means every date stays selectable, and the
        // enquiry still reaches AJ. Better than blocking the whole calendar.
        setStatus("error");
      });

    return () => {
      live = false;
    };
  }, []);

  const bookedKeys = useMemo(() => {
    const keys = new Set();
    for (const r of ranges) {
      if (!r?.start || !r?.end) continue;
      const cursor = fromIso(r.start);
      const stop = fromIso(r.end);
      let guard = 0;
      while (cursor < stop && guard < 400) {
        keys.add(iso(cursor));
        cursor.setDate(cursor.getDate() + 1);
        guard += 1;
      }
    }
    return keys;
  }, [ranges]);

  const bookedDates = useMemo(
    () => Array.from(bookedKeys).map(fromIso),
    [bookedKeys]
  );

  const isBooked = useMemo(
    () => (date) => bookedKeys.has(iso(date)),
    [bookedKeys]
  );

  /** Checks nights only, so a checkout landing on a booked check-in is fine. */
  const rangeHasBooked = useMemo(
    () => (from, to) => {
      if (!from || !to) return false;
      const cursor = new Date(from);
      let guard = 0;
      while (cursor < to && guard < 400) {
        if (bookedKeys.has(iso(cursor))) return true;
        cursor.setDate(cursor.getDate() + 1);
        guard += 1;
      }
      return false;
    },
    [bookedKeys]
  );

  return { status, bookedDates, isBooked, rangeHasBooked };
}
